import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react'

import { ConnectionCard } from '@/components/connectionCard/ConnectionCard'
import {
  filterInvitationsSent,
  getArryOfIdsFromConnection,
  validateConnectionExistsById,
} from '@/components/requestConnectionButton/validateConnectionSentReceived'
import { LoaderSpinner } from '@/components/ui/LoaderSpinner.component'
import {
  IRecommendation,
  ProfileRecommendationType,
} from '@/interfaces/recommendation'
import { IUser } from '@/interfaces/user'
import { errorMiddleware } from '@/services/errorMiddleware'
import { getUserRecommendations, getUsersByIds } from '@/services/user'
import { environment } from '@/shared/consts/environment'
import { useProfilePage } from '@/shared/hooks/profilePage'
import { useQuery } from '@/shared/hooks/useQuery'
import axios from 'axios'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { useUser } from 'containers/user/hooks/useUser'
import { UserContext } from 'context'
import { RecommendationList } from './RecommendationList'
import { RecommendationListEmpty } from './invitations/RecommendationListSectionEmpty'

const apiUrl = `${environment.apiUrl}/api/v1/users`

export const InvitesConnections: React.FC = () => {
  return (
    <div className="flex flex-column gap-30">
      <InvitationsList />
      <RecommendationsList />
    </div>
  )
}

const InvitationsList = () => {
  const { profile } = useProfilePage()
  const { connectionsReceived } = profile

  const invitationsIds = useMemo(() => {
    return getArryOfIdsFromConnection(connectionsReceived)
  }, [connectionsReceived])

  const {
    data: invitations,
    isCompleted,
    isLoading,
    setData,
  } = useQuery<IUser[]>({
    queryMethod: getUsersByIds,
    queryArguments: invitationsIds,
    useFetchEffect: invitationsIds?.length > 0,
  })

  //
  const filteredInvitations = useMemo(() => {
    return invitations.filter((invitation) =>
      validateConnectionExistsById({
        id: invitation?.id,
        connections: connectionsReceived,
      })
    )
  }, [invitations, connectionsReceived])

  const onActionSuccess = useCallback(
    (invitationId: string) => {
      setData(
        filteredInvitations.filter((invitation) => invitation !== invitationId)
      )
    },
    [filteredInvitations, setData]
  )

  if (!connectionsReceived) return null

  if (isLoading && !isCompleted) {
    return (
      <span className="cl-text-secondary fw-md fs-18">
        Invitations{' '}
        <span className="cl-pink-primary fs-20">
          <LoaderSpinner />
        </span>
      </span>
    )
  }

  return (
    <div>
      <span className="cl-text-secondary fw-md fs-18">
        Invitations{' '}
        <span className="cl-pink-primary fs-20">
          {filteredInvitations?.length.toLocaleString() || 0}
        </span>
      </span>
      <div className="flex flex-column gap-14 mt-20">
        {filteredInvitations.map((invitation) => (
          <ConnectionCard connection={invitation} key={invitation.id}>
            <ConnectionCard.AcceptConnectionButton
              onSuccess={onActionSuccess}
            />
            <ConnectionCard.RejectConnectionButton
              onSuccess={onActionSuccess}
            />
          </ConnectionCard>
        ))}
      </div>
    </div>
  )
}

// const PendingRequestsList = () => {
//   // TODO: this code is not been used should be removed ???
//   const { profile } = useProfilePage()
//   const { connectionsSent } = profile
//   const { data: pendingRequests, isLoading } = useQuery<IUser[]>({
//     queryMethod: getUsersByIds,
//     queryArguments: connectionsSent,
//     useFetchEffect: connectionsSent?.length > 0,
//   })

//   if (!connectionsSent) return null

//   if (isLoading) {
//     return (
//       <span className="cl-text-secondary fw-md fs-18">
//         Invitations{' '}
//         <span className="cl-pink-primary fs-20">
//           <LoaderSpinner />
//         </span>
//       </span>
//     )
//   }
//   return (
//     <div>
//       <span className="cl-text-secondary fw-md fs-18">
//         Pending Requests{' '}
//         <span className="cl-pink-primary fs-20">
//           {connectionsSent?.length || 0}
//         </span>
//       </span>
//       <div className="flex flex-column gap-14 mt-20">
//         {pendingRequests.map((invitation, index) => (
//           <ConnectionCard
//             connection={invitation}
//             key={`${invitation.id}-${index}`}
//           >
//             <ConnectionCard.ViewProfileButton />
//           </ConnectionCard>
//         ))}
//       </div>
//     </div>
//   )
// }

// TODO: context should be moved to an external file
// TODO: to many components into the same file, is not an error but is not a good practice
type DismissedRecommendationsContextType = {
  dismissedRecommendations: string[]
  dismissRecommendation: (value: string) => void
}

const DismissedRecommendationsContext = createContext(
  {} as DismissedRecommendationsContextType
)

export const DismissedRecommendationsProvider = ({ children }) => {
  const [dismissed, setDismissed] = useReducer(
    (state: string[], dismissedId: string) => [...state, dismissedId],
    []
  )
  const { user } = useContext(UserContext)
  const dismissRecommendation = async (dismissedID: string) => {
    try {
      // Make an API call to persist the dismissal
      // Include requestorId and dismissedId as parameters in the API call
      const requestorId = user.id
      const dismissedId = dismissedID
      await axios.get(
        `${apiUrl}/dismiss/recommendation?requestorId=${requestorId}&dismissedId=${dismissedId}`
      )
      // If the API call is successful, update the state
      setDismissed(dismissedID)
    } catch (error) {
      // this swallows the error - may need to rethink that @todo ?
      const err = errorMiddleware(error)
      console.log(err)
    }
  }

  return (
    <DismissedRecommendationsContext.Provider
      value={{
        dismissedRecommendations: dismissed,
        dismissRecommendation: dismissRecommendation,
      }}
    >
      {children}
    </DismissedRecommendationsContext.Provider>
  )
}

export const RecommendationsList = () => {
  const { user } = useUser()
  const { profile } = useProfile()
  const { dismissRecommendation, dismissedRecommendations } = useContext(
    DismissedRecommendationsContext
  )
  const {
    data: recommendations,
    isCompleted,
    isLoading,
    setData,
  } = useQuery<IRecommendation[]>({
    queryMethod: getUserRecommendations,
    queryArguments: profile?.id,
    useFetchEffect: !!profile?.id,
    initialData: [],
  })

  const filteredRecommendations = useMemo(() => {
    return (
      filterInvitationsSent(
        recommendations?.filter((recommendation) => {
          const isRecommendationInConnectionsSent =
            validateConnectionExistsById({
              id: recommendation?.id,
              connections: user?.connectionsSent || [],
            })

          const isRecommendationInConnectionsReceived =
            validateConnectionExistsById({
              id: recommendation?.id,
              connections: user?.connectionsReceived || [],
            })

          return (
            !dismissedRecommendations.includes(recommendation?.id) &&
            !user?.connections?.includes(recommendation?.id) &&
            !isRecommendationInConnectionsReceived &&
            !isRecommendationInConnectionsSent
          )
        }),
        user as IUser
      ) || []
    )
  }, [dismissedRecommendations, recommendations, user])

  const hotAndColdProfileRecommendations = useMemo(() => {
    return filteredRecommendations.filter(
      (recommendation) =>
        recommendation.recommendationType !==
        ProfileRecommendationType.GPP_PROFILE
    )
  }, [filteredRecommendations])

  const onConnect = useCallback(
    (recommendationId: string) => {
      setData(
        recommendations.filter(
          (recommendation) => recommendation.id !== recommendationId
        )
      )
    },
    [recommendations, setData]
  )

  if (!filteredRecommendations.length && isCompleted) {
    return <RecommendationListEmpty />
  }

  if (isLoading && !isCompleted) {
    return (
      <span className="cl-text-secondary fw-md fs-18">
        Recommended from your Interests{' '}
        <span className="cl-pink-primary fs-20 mt-40 block">
          <LoaderSpinner />
        </span>
      </span>
    )
  }

  return (
    <div>
      {/* <RecommendationList
        list={gpProfileRecommendations}
        title="Recommended from your Contacts"
        onConnect={onConnect}
        dismissRecommendation={dismissRecommendation}
      /> */}
      <RecommendationList
        list={hotAndColdProfileRecommendations}
        title="Recommended from your Interests"
        onConnect={onConnect}
        dismissRecommendation={dismissRecommendation}
      />
      <RecommendationList
        list={filteredRecommendations}
        title="All Recommendations"
        onConnect={onConnect}
        dismissRecommendation={dismissRecommendation}
      />
    </div>
  )
}
